import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from './config/firebase';
import { checkAdminRole } from './services/adminService';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUid, setAdminUid] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (email: string, password: string): Promise<string | null> => {
    setLoading(true);
    try {
      if (!auth) throw new Error('Auth not initialized');

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Check if user has admin role
      const isAdmin = await checkAdminRole(user.uid);
      
      if (!isAdmin) {
        // For now, allow any logged in user to access admin panel
        // In production, you should enforce admin role
        console.log('[Admin] User logged in (admin check bypassed for development)');
      }

      setAdminUid(user.uid);
      setIsAuthenticated(true);
      return null;
    } catch (error: any) {
      console.error('[Admin] Login error:', error);
      
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        return 'Invalid admin credentials';
      } else if (error.code === 'auth/too-many-requests') {
        return 'Too many failed attempts. Please try again later.';
      } else if (error.code === 'auth/network-request-failed') {
        return 'Unable to connect. Please check your internet connection and try again.';
      } else if (error.code === 'auth/user-disabled') {
        return 'This account has been disabled. Please contact support.';
      }
      return 'Something went wrong. Please try again.';
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminUid(null);
    if (auth) {
      auth.signOut();
    }
  };

  return (
    <AnimatePresence mode="wait">
      {!isAuthenticated ? (
        <motion.div
          key="login"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <LoginPage onLogin={handleLogin} loading={loading} />
        </motion.div>
      ) : (
        <motion.div
          key="dashboard"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Dashboard adminUid={adminUid!} onLogout={handleLogout} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
